import { EProjectPermission } from "@frontend-shared/types";
import { useMemo } from "react";

import { useAuth } from "./use-auth";
import { usePermissionCheck } from "./use-permission";

export interface ISkillPermissions {
  canReadPrivate: boolean;
  canWritePrivate: boolean;
  canDeletePrivate: boolean;
  canReadPublic: boolean;
  canWritePublic: boolean;
  canDeletePublic: boolean;
  canReadAny: boolean;
  canCreatePrivate: boolean;
  canCreatePublic: boolean;
  canCreateAny: boolean;
  // NOTE: NSAI-3309 — private_skill_share:write. Sharing is a permission of its
  // own: a role may manage its own private skills without being allowed to hand
  // them to anybody else.
  canShare: boolean;
  // NOTE: NSAI-3311 — organization_skill:read / :curate. Organization-scoped
  // rather than per-visibility, because a catalog entry belongs to the
  // organization and not to the project the request happens to carry.
  canReadOrgCatalog: boolean;
  canCurateOrgCatalog: boolean;
  // NOTE: Installing writes a PRIVATE skill into the current project, so it needs
  // the catalog read AND the ordinary private-skill create permissions. Derived
  // here so no screen re-combines the three and gets it subtly wrong.
  canInstallOrgCatalog: boolean;
  // NOTE: NSAI-3309 — true while the project's permission payload is still UNKNOWN.
  // `usePermissionCheck` answers `false` for everything until it arrives, which is
  // indistinguishable from a denial, so a caller that acts on a false without
  // checking this reports a denial it has no evidence for.
  //
  // Today nothing reaches that state: `GlobalLoader` holds the whole app back while
  // `useAuth().isLoading` is true, and that includes the permissions query. This is
  // therefore defence in depth, not a live bug — it makes the hook honest about what
  // it knows so a caller cannot be wrong by accident if that gate ever moves or a
  // screen renders outside it. Mobile carries the same field and needs it, because
  // its guard is per-route rather than global.
  isLoading: boolean;
}

// NOTE: NSAI-3306 — Agent Skills RBAC for the UI, resolved against the CURRENTLY
// ACTIVE project. Skills deliberately have no generic `skill:*` permission: a
// role may manage its own private skills without gaining the project-wide public
// ones, so every derived flag below stays split by visibility. The server
// re-checks all of this; these flags only decide what is worth rendering.
export const useSkillPermissions = (): ISkillPermissions => {
  const [
    canReadPrivate,
    canWritePrivate,
    canDeletePrivate,
    canReadPublic,
    canWritePublic,
    canDeletePublic,
    canShare,
    canReadOrgCatalog,
    canCurateOrgCatalog,
  ] = usePermissionCheck([
    EProjectPermission.PRIVATE_SKILL_READ,
    EProjectPermission.PRIVATE_SKILL_WRITE,
    EProjectPermission.PRIVATE_SKILL_DELETE,
    EProjectPermission.PUBLIC_SKILL_READ,
    EProjectPermission.PUBLIC_SKILL_WRITE,
    EProjectPermission.PUBLIC_SKILL_DELETE,
    EProjectPermission.PRIVATE_SKILL_SHARE_WRITE,
    EProjectPermission.ORGANIZATION_SKILL_READ,
    EProjectPermission.ORGANIZATION_SKILL_CURATE,
  ]);

  const { projectPermissions, isPersonalProject } = useAuth();

  return useMemo(
    () => ({
      canReadPrivate,
      canWritePrivate,
      canDeletePrivate,
      canReadPublic,
      canWritePublic,
      canDeletePublic,
      canReadAny: canReadPrivate || canReadPublic,
      // NOTE: Creating a skill of a visibility requires BOTH reading and writing
      // it — a role that may write but not read would author something it then
      // cannot open.
      canCreatePrivate: canReadPrivate && canWritePrivate,
      // NOTE: NSAI-5808 — `&& !isPersonalProject` for the same structural reason
      // spelled out below: a personal project has no organization, so the server
      // cannot host a public skill there whatever the role says. Without this the
      // wildcard admin role answered true, the edit form offered the visibility
      // toggle, and the save came back 200 with the skill still private — the
      // server resolved the requested visibility to false rather than refusing it.
      canCreatePublic: canReadPublic && canWritePublic && !isPersonalProject,
      canCreateAny:
        (canReadPrivate && canWritePrivate) ||
        (canReadPublic && canWritePublic && !isPersonalProject),
      // NOTE: NSAI-3309/NSAI-3311 — NEITHER capability exists in a personal
      // project, and a permission check cannot tell you that. The personal
      // project's member holds a WILDCARD admin role, so every permission below
      // answers true there; the constraint is structural, not role-based.
      //
      // Sharing: a personal project has exactly one member, and a skill's owner
      // is excluded from its own candidate list, so the recipient list is always
      // empty. The dialog opened onto nothing.
      //
      // Catalog: a personal project has no organization (`organizationId` is
      // null), and the server refuses the catalog for one outright —
      // `OrganizationSkillAccessService` answers CATALOG_NOT_FOUND before it
      // looks at any permission. Offering the entry point meant navigating to a
      // page that could only error.
      canShare: canShare && !isPersonalProject,
      canReadOrgCatalog: canReadOrgCatalog && !isPersonalProject,
      canCurateOrgCatalog: canCurateOrgCatalog && !isPersonalProject,
      canInstallOrgCatalog:
        canReadOrgCatalog &&
        canReadPrivate &&
        canWritePrivate &&
        !isPersonalProject,
      isLoading: projectPermissions === null,
    }),
    [
      canReadPrivate,
      canWritePrivate,
      canDeletePrivate,
      canReadPublic,
      canWritePublic,
      canDeletePublic,
      canShare,
      canReadOrgCatalog,
      canCurateOrgCatalog,
      projectPermissions,
      isPersonalProject,
    ],
  );
};
